import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Save, PhoneCall, PencilLine, CalendarDays, History } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const BLUE = "#3b9fe0", AMBER = "#f2c037";

function StackChart({ rows, testid, title }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <h3 className="font-heading font-semibold text-sm text-slate-800 mb-2">{title}</h3>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={rows} margin={{ top: 8, right: 8, left: -28, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke="#f1f5f9" />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval={0} angle={-30} height={42} textAnchor="end" />
          <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} />
          <Tooltip />
          <Bar dataKey="logged" name="Logged calls" stackId="a" fill={BLUE} isAnimationActive={false} />
          <Bar dataKey="manual" name="Manual entries" stackId="a" fill={AMBER} radius={[3, 3, 0, 0]} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
      <div className="flex items-center justify-center gap-4 mt-1">
        <span className="flex items-center gap-1.5 text-[11px] text-slate-500"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: BLUE }} /> Logged in Daily Calls</span>
        <span className="flex items-center gap-1.5 text-[11px] text-slate-500"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: AMBER }} /> Manual entries</span>
      </div>
    </Card>
  );
}


const Split = ({ v, testid }) => (
  <td className="px-2 py-2 text-right whitespace-nowrap" data-testid={testid}>
    <span className={`text-sm font-mono ${v?.total ? "text-slate-900 font-semibold" : "text-slate-300"}`}>{v?.total ?? 0}</span>
    <span className="block text-[10px] font-mono text-slate-400">{v?.manual ?? 0} M · {v?.checkin ?? 0} C</span>
  </td>
);

function CallSummaryTable({ data }) {
  if (!data) return null;
  const rows = data.rows || [];
  const periods = data.periods || [];
  return (
    <Card className="p-4" data-testid="dsr-summary-table">
      <h3 className="font-heading font-semibold text-sm text-slate-800 flex items-center gap-1.5 mb-3">
        <CalendarDays className="h-4 w-4 text-brand" /> Calls per salesperson
        <span className="text-[11px] font-normal text-slate-400 ml-1">M = manual (Log a Call + DSR numbers) · C = Check In / Check Out · weeks run Mon–Sun</span>
      </h3>
      <div className="overflow-x-auto rounded-md border border-slate-100">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 text-left">
              <th className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Salesperson</th>
              <th className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Region</th>
              {periods.map((p) => (
                <th key={p.key} className={`px-2 py-2 text-xs uppercase tracking-wider text-slate-500 font-semibold text-right ${p.key === "yesterday" ? "border-r border-slate-200" : ""}`}>
                  {p.label}<span className="block text-[10px] normal-case font-normal text-slate-400">{p.sub}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r, i) => (
              <tr key={r.user_id} data-testid={`dsr-summary-row-${i}`}>
                <td className="px-3 py-2 text-sm font-medium text-slate-700 whitespace-nowrap">{r.name}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{r.region}</td>
                {periods.map((p) => <Split key={p.key} v={r[p.key]} testid={`dsr-summary-${i}-${p.key}`} />)}
              </tr>
            ))}
            {rows.length > 1 && (
              <tr className="bg-slate-50 font-semibold" data-testid="dsr-summary-total">
                <td className="px-3 py-2 text-sm text-slate-800" colSpan={2}>Total</td>
                {periods.map((p) => <Split key={p.key} v={data.totals?.[p.key]} testid={`dsr-summary-total-${p.key}`} />)}
              </tr>
            )}
            {rows.length === 0 && <tr><td colSpan={2 + periods.length} className="text-center py-8 text-sm text-slate-400">No team members found.</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function DSRHistory() {
  const { data, isLoading } = useQuery({ queryKey: ["dsr-history"], queryFn: async () => (await api.get("/calls/dsr/history")).data });
  if (isLoading) return <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div>;
  const weeks = data?.weeks || [];
  return (
    <div className="space-y-4" data-testid="dsr-history">
      <p className="text-xs text-slate-500">Weeks older than the last 4 weeks that have call data · M = manual (Log a Call + DSR numbers) · C = Check In / Check Out</p>
      {weeks.length === 0 && <Card className="p-8 text-center text-sm text-slate-400" data-testid="dsr-history-empty">No historical call data before the last 4 weeks yet.</Card>}
      {weeks.map((w) => (
        <Card key={w.from} className="p-4" data-testid={`dsr-history-week-${w.from}`}>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-heading font-semibold text-sm text-slate-800 flex items-center gap-1.5"><History className="h-4 w-4 text-brand" /> {w.label}</h3>
            <span className="text-xs text-slate-500 font-mono">{w.totals.total} calls · {w.totals.manual} M · {w.totals.checkin} C</span>
          </div>
          <table className="w-full">
            <thead><tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-3 py-1.5 font-semibold">Salesperson</th><th className="px-3 py-1.5 font-semibold">Region</th>
              <th className="px-3 py-1.5 font-semibold text-right">Manual</th><th className="px-3 py-1.5 font-semibold text-right">Check-in</th><th className="px-3 py-1.5 font-semibold text-right">Total</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {w.rows.map((r) => (
                <tr key={r.user_id}>
                  <td className="px-3 py-1.5 text-sm font-medium text-slate-700">{r.name}</td>
                  <td className="px-3 py-1.5 text-xs text-slate-500">{r.region}</td>
                  <td className="px-3 py-1.5 text-sm font-mono text-right">{r.manual}</td>
                  <td className="px-3 py-1.5 text-sm font-mono text-right">{r.checkin}</td>
                  <td className="px-3 py-1.5 text-sm font-mono text-right font-semibold">{r.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ))}
    </div>
  );
}

function BackfillCard() {
  const qc = useQueryClient();
  const [target, setTarget] = useState("");
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["dsr-backfill", target],
    queryFn: async () => (await api.get("/calls/dsr/backfill", { params: { user_id: target, days: 7 } })).data,
  });
  const rows = data?.rows || [];
  const dirty = Object.keys(edits).length > 0;
  const saveAll = async () => {
    const entries = rows.filter((r) => edits[r.date] !== undefined).map((r) => ({ date: r.date, calls: edits[r.date], remarks: r.remarks }));
    if (!entries.length) return;
    setSaving(true);
    try {
      await api.put("/calls/dsr/backfill", { user_id: data?.user_id, entries });
      toast.success(`Backfilled ${entries.length} day${entries.length > 1 ? "s" : ""}`);
      setEdits({});
      ["dsr-backfill", "dsr", "dsr-day", "dsr-summary"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Card className="p-4" data-testid="dsr-backfill-card">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <h3 className="font-heading font-semibold text-sm text-slate-800 flex items-center gap-1.5">
          <History className="h-4 w-4 text-brand" /> Backfill past days
          <span className="text-[11px] font-normal text-slate-400 ml-1">Enter how many calls you made on each day (last 7 days)</span>
        </h3>
        <div className="flex items-center gap-2">
          {data?.users?.length > 0 && (
            <Select value={target || data.user_id} onValueChange={(v) => { setTarget(v); setEdits({}); }}>
              <SelectTrigger className="h-8 w-[190px] text-xs" data-testid="dsr-backfill-user"><SelectValue /></SelectTrigger>
              <SelectContent>{data.users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button size="sm" className="h-8 gap-1.5 bg-brand hover:bg-brand/90 text-white" disabled={!dirty || saving} onClick={saveAll} data-testid="dsr-backfill-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save all
          </Button>
        </div>
      </div>
      {isLoading ? <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div> : (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {rows.map((r, i) => {
            const val = edits[r.date] ?? r.manual;
            return (
              <div key={r.date} className={`rounded-lg border p-2.5 ${edits[r.date] !== undefined ? "border-brand/40 bg-brand/5" : "border-slate-200 bg-white"} ${r.is_sunday ? "opacity-70" : ""}`} data-testid={`dsr-backfill-day-${i}`}>
                <p className="text-[11px] font-semibold text-slate-600">{r.label}</p>
                <p className="text-[10px] text-slate-400 mb-1.5">{r.logged} logged</p>
                <input type="number" min="0" max="500" value={val}
                  onChange={(e) => setEdits((x) => ({ ...x, [r.date]: e.target.value === "" ? 0 : Math.max(0, Number(e.target.value)) }))}
                  className="w-full h-8 px-2 text-center border border-slate-200 rounded bg-white text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand"
                  data-testid={`dsr-backfill-input-${i}`} />
                <p className="text-[10px] text-slate-400 mt-1 text-center">manual calls</p>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

export function DSRAnalytics() {
  const qc = useQueryClient();
  const [day, setDay] = useState(() => new Date().toISOString().slice(0, 10));
  const [edits, setEdits] = useState({});
  const [savingId, setSavingId] = useState(null);

  const { data: dsr, isLoading } = useQuery({
    queryKey: ["dsr"], queryFn: async () => (await api.get("/calls/dsr")).data,
  });
  const { data: summary } = useQuery({
    queryKey: ["dsr-summary"], queryFn: async () => (await api.get("/calls/dsr/summary")).data,
  });
  const { data: dayData } = useQuery({
    queryKey: ["dsr-day", day],
    queryFn: async () => (await api.get("/calls/dsr/day", { params: { date_str: day } })).data,
  });

  if (isLoading || !dsr) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;

  const setEdit = (id, k, v) => setEdits((x) => ({ ...x, [id]: { ...x[id], [k]: v } }));
  const saveRow = async (row) => {
    const e = edits[row.user_id] || {};
    setSavingId(row.user_id);
    try {
      await api.put("/calls/dsr/entry", { date: day, user_id: row.user_id, calls: e.calls ?? row.manual, remarks: e.remarks ?? row.remarks });
      toast.success(`${row.name} — DSR updated`);
      setEdits((x) => { const n = { ...x }; delete n[row.user_id]; return n; });
      qc.invalidateQueries({ queryKey: ["dsr"] });
      qc.invalidateQueries({ queryKey: ["dsr-day"] });
      qc.invalidateQueries({ queryKey: ["dsr-summary"] });
    } catch (err) { toast.error(apiError(err)); } finally { setSavingId(null); }
  };

  const rows = dayData?.rows || [];

  return (
    <div className="space-y-4" data-testid="dsr-analytics">
      <CallSummaryTable data={summary} />
      <BackfillCard />
      <div className="grid md:grid-cols-2 gap-4">
        <StackChart rows={dsr.per_day} title="Calls per day (last 14 days)" testid="dsr-day-chart" />
        <StackChart rows={dsr.per_week} title="Calls per week (last 8 weeks)" testid="dsr-week-chart" />
      </div>

      <Card className="p-4" data-testid="dsr-day-table">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <h3 className="font-heading font-semibold text-sm text-slate-800 flex items-center gap-1.5">
            <PhoneCall className="h-4 w-4 text-brand" /> Team calls on
            <Input type="date" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} className="h-8 w-[150px] ml-1" data-testid="dsr-date-input" />
          </h3>
          <p className="text-[11px] text-slate-400 flex items-center gap-1"><PencilLine className="h-3 w-3" />
            {dayData?.can_edit_all ? "You can edit the numbers of everyone shown" : "You can edit your own row"}</p>
        </div>
        <div className="overflow-x-auto rounded-md border border-slate-100">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 text-left">
                {["Salesperson", "Region", "Logged", "Manual", "Total", "Remarks", ""].map((h, i) => (
                  <th key={i} className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r, i) => {
                const canEdit = dayData?.can_edit_all || r.user_id === dayData?.me;
                const e = edits[r.user_id] || {};
                const dirty = e.calls !== undefined || e.remarks !== undefined;
                return (
                  <tr key={r.user_id} data-testid={`dsr-row-${i}`}>
                    <td className="px-3 py-2 text-sm font-medium text-slate-700">{r.name}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{r.region}</td>
                    <td className="px-3 py-2 text-sm font-mono text-slate-600" title="Auto-counted from Daily Calls logs">{r.logged}</td>
                    <td className="px-3 py-2">
                      {canEdit ? (
                        <input type="number" min="0" value={e.calls ?? r.manual}
                          onChange={(ev) => setEdit(r.user_id, "calls", ev.target.value === "" ? 0 : Number(ev.target.value))}
                          className="w-16 h-7 px-1.5 text-center border border-slate-200 rounded bg-white text-sm focus:outline-none focus:ring-1 focus:ring-brand"
                          data-testid={`dsr-manual-input-${i}`} />
                      ) : <span className="text-sm font-mono">{r.manual}</span>}
                    </td>
                    <td className="px-3 py-2 text-sm font-mono font-bold text-slate-900">{(e.calls ?? r.manual) + r.logged}</td>
                    <td className="px-3 py-2">
                      {canEdit ? (
                        <input value={e.remarks ?? r.remarks}
                          onChange={(ev) => setEdit(r.user_id, "remarks", ev.target.value)}
                          placeholder="—"
                          className="w-full min-w-[120px] h-7 px-2 border border-slate-200 rounded bg-white text-xs focus:outline-none focus:ring-1 focus:ring-brand"
                          data-testid={`dsr-remarks-input-${i}`} />
                      ) : <span className="text-xs text-slate-500">{r.remarks || "—"}</span>}
                    </td>
                    <td className="px-2 py-2">
                      {canEdit && (
                        <Button size="sm" variant="outline" className="h-7 px-2 gap-1" disabled={!dirty || savingId === r.user_id}
                          onClick={() => saveRow(r)} data-testid={`dsr-save-${i}`}>
                          {savingId === r.user_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-sm text-slate-400">No team members found.</td></tr>}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-slate-400 mt-2">Logged = auto-counted from the Daily Calls module · Manual = calls made but not recorded (editable).</p>
      </Card>
    </div>
  );
}
